import React from "react";
import { connect } from "react-redux";
import { path } from "Constants";
import { resetError, defineScopeLoading,pendingChanges } from "actions/spendActions";
import { GetRiseSavingDetails, UpdateRiseSavingDetails } from "actions/kpiActions";
import classNames from "classnames";

class SavingOptions extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      groupedData: {},
      originalGroupedData: {},
      isLoading: false,
      isError: false,
      errorMsg: "",
      isSaveInProgress: false,
      hasChanges: false,
      responseMsg: "",
    };

    // refs for parent checkboxes so we can set .indeterminate
    this.parentCheckboxRefs = {};

    this.loadData = this.loadData.bind(this);
    this.onSave = this.onSave.bind(this);
    this.toggleSavingAvailability = this.toggleSavingAvailability.bind(this);
    this.toggleHeaderAvailability = this.toggleHeaderAvailability.bind(this);
    this.reloadParentIndeterminate = this.reloadParentIndeterminate.bind(this);
  }

  componentDidMount() {
    this.loadData();
  }

  componentDidUpdate(prevProps, prevState) {
    // whenever groupedData changes, update indeterminate state on parent checkboxes
    if (prevState.groupedData !== this.state.groupedData) {
      this.reloadParentIndeterminate();
    }
  }

  // set indeterminate property for each parent checkbox DOM node
  reloadParentIndeterminate() {
    const { groupedData } = this.state;
    if (!groupedData) return;

    Object.keys(groupedData).forEach((groupKey) => {
      const ref = this.parentCheckboxRefs[groupKey];
      const items = groupedData[groupKey].items || [];
      const allChecked = items.length > 0 && items.every((it) => !!it.fieldAvailable);
      const noneChecked = items.length === 0 || items.every((it) => !it.fieldAvailable);
      const indeterminate = !allChecked && !noneChecked;

      if (ref) {
        try {
          ref.indeterminate = indeterminate;
          // keep native checked in sync with "allChecked" so clicking works properly
          // we still set React's checked prop to allChecked in render
        } catch (err) {
          // ignore if DOM node unavailable
        }
      }
    });
  }

  async loadData() {
    try {
      const spendDetails = this.props && this.props.spendDetails;
      const user = this.props && this.props.user && this.props.user.user && this.props.user.user.Email;
      if (!spendDetails || !spendDetails.currentActiveRequest || !user) {
        this.setState({ groupedData: {}, isError: true, errorMsg: "Missing required props." });
        return;
      }

      const dmcId = spendDetails.currentActiveRequest.DMC_ID;
      const vsId = spendDetails.currentActiveRequest.VS_ID;

      if (typeof this.props.defineScopeLoading === "function") {
        this.props.defineScopeLoading(true);
      }
      this.setState({ isLoading: true, isError: false, errorMsg: "", hasChanges: false });

      const response = await this.props.GetRiseSavingDetails(dmcId, vsId, user);
      const result = response && response.data && response.data.Result.Table;
      if (!Array.isArray(result) || result.length === 0) {
        if (typeof this.props.defineScopeLoading === "function") {
          this.props.defineScopeLoading(false);
        }
        this.setState({ groupedData: {}, isLoading: false });
        return;
      }

      const grouped = {};
      result.forEach((r, idx) => {
        const saving = r.SAVING_FIELD_NAME || "(No Saving Field)";
        if (!grouped[saving]) grouped[saving] = { items: [] };
        grouped[saving].items.push({
          id: `${saving}-${idx}`,
          headerName: r.HEADER_FIELD_NAME,
          // normalize FIELD_AVAILABLE to boolean
          fieldAvailable:
            typeof r.FIELD_AVAILABLE === "string"
              ? r.FIELD_AVAILABLE.toLowerCase() === "yes"
              : !!r.FIELD_AVAILABLE,
        });
      });

      Object.keys(grouped).forEach((k) => {
        grouped[k].available = grouped[k].items.length > 0 && grouped[k].items.every((it) => it.fieldAvailable);
      });

      // store original snapshot to compare later
      const snapshot = JSON.parse(JSON.stringify(grouped));
      this.setState({ groupedData: grouped, originalGroupedData: snapshot, isLoading: false, hasChanges: false });
      if (typeof this.props.defineScopeLoading === "function") {
        this.props.defineScopeLoading(false);
      }
    } catch (error) {
      if (typeof this.props.defineScopeLoading === "function") {
        this.props.defineScopeLoading(false);
      }
      const msg = (this.props && this.props.content && this.props.content.error) || (error && error.message) || "Error";
      this.setState({ groupedData: {}, isLoading: false, isError: true, errorMsg: msg });
    }
  }

  // mark hasChanges whenever a change is made
  toggleSavingAvailability(savingKey) {
    this.setState((prev) => {
      const groupedData = { ...prev.groupedData };
      const group = { ...groupedData[savingKey] };
      const newAvailable = !group.available;
      group.available = newAvailable;
      group.items = group.items.map((it) => ({ ...it, fieldAvailable: newAvailable }));
      groupedData[savingKey] = group;
      this.props.pendingChanges(true);
      return { groupedData, hasChanges: true,  responseMsg: "Pending changes. Click Save to update." };
    }, () => {
      // update indeterminate visuals after change
      this.reloadParentIndeterminate();
    });
  }

  toggleHeaderAvailability(savingKey, itemIndex) {
    this.setState((prev) => {
      const groupedData = { ...prev.groupedData };
      const group = { ...groupedData[savingKey] };
      const items = [...group.items];
      items[itemIndex] = { ...items[itemIndex], fieldAvailable: !items[itemIndex].fieldAvailable };
      group.items = items;
      // parent is considered available only if all children are available
      group.available = items.length > 0 && items.every((it) => it.fieldAvailable);
      groupedData[savingKey] = group;
      this.props.pendingChanges(true);
      return { groupedData, hasChanges: true, responseMsg: "Pending changes. Click Save to update." };
    }, () => {
      // update indeterminate visuals after change
      this.reloadParentIndeterminate();
    });
  }

  // Save only changed items (uses UpdateRiseSavingDetails from props)
  async onSave() {
    try {
      const vsId = this.props.spendDetails && this.props.spendDetails.currentActiveRequest && this.props.spendDetails.currentActiveRequest.VS_ID;
      const dmcId = this.props.spendDetails && this.props.spendDetails.currentActiveRequest && this.props.spendDetails.currentActiveRequest.DMC_ID;
      const userid = this.props.user.user.Email;
      if (!vsId) return;

      // ---------- VALIDATION ----------
      const { groupedData } = this.state;
      // const spendChecked = groupedData['Spend Compliance'] && groupedData['Spend Compliance'].available;
      // const processChecked = groupedData['Process Improvement'] && groupedData['Process Improvement'].available;

      // if (!spendChecked && !processChecked) {
      //   // show validation error and abort save
      //   this.setState({ errorMsg: "At least one of 'Spend Compliance' or 'Process Improvement' must be checked.", isError: true });
      //   return;
      // }
      // clear previous validation error if any
      this.setState({ errorMsg: "", isError: false });

      // ---------- COLLECT UPDATES ----------
      const { originalGroupedData } = this.state;
      const updates = [];

      Object.keys(groupedData).forEach((savingKey) => {
        const items = groupedData[savingKey].items || [];
        const origItems = (originalGroupedData[savingKey] && originalGroupedData[savingKey].items) || [];
        items.forEach((it) => {
          const orig = origItems.find((o) => o.id === it.id);
          const origAvailable = orig ? !!orig.fieldAvailable : false;
          if (!!it.fieldAvailable !== origAvailable) {
            updates.push({
              VS_ID: vsId,
              DMC_ID:  dmcId,
              USER_ID: userid,
              SAVING_TAB_NAME: savingKey,
              HEADER_FIELD_NAME: it.headerName,
              FIELD_AVAILABLE: it.fieldAvailable ? "Yes" : "No",
            });
          }
        });
      });

      if (updates.length === 0) {
        this.setState({ responseMsg: "No changes to save." });
        return;
      }

      this.setState({ isSaveInProgress: true, responseMsg: "", errorMsg: "" });

      // // Call API for each changed item (you can change to batch if API supports it)
      // const promises = updates.map((u) =>
      //   this.props.UpdateRiseSavingDetails(u.VS_ID,u.DMC_ID, u.SAVING_TAB_NAME, u.HEADER_FIELD_NAME, u.FIELD_AVAILABLE)
      // );

      this.props.UpdateRiseSavingDetails(updates).then((response) => {
        if (response.status == 200) {
          const newSnapshot = JSON.parse(JSON.stringify(this.state.groupedData));
      this.setState({
        originalGroupedData: newSnapshot,
        isSaveInProgress: false,
        hasChanges: false,
        responseMsg: "Saved successfully.",
        isError: false,
      }, () => {
        this.props.pendingChanges(null);
        // notify parent to reload tabs so show/hide updates at runtime and focus Options
        if (typeof this.props.onSaved === 'function') {
          this.props.onSaved();
        }
        // refresh indeterminate visuals after save commit
        this.reloadParentIndeterminate();
      });
        }
      });

      // await Promise.all(promises);

      // on success reset original snapshot and hasChanges
      
    } catch (err) {
      const msg = (err && err.message) || "Save failed.";
      this.setState({ errorMsg: msg, isSaveInProgress: false, isError: true });
    }
  }

  render() {
    const { groupedData, isLoading, isError, errorMsg, isSaveInProgress, hasChanges, responseMsg } = this.state;
    if (isLoading) return <div>Loading...</div>;
    if (!groupedData || Object.keys(groupedData).length === 0) {
      return (
        <div>
          <h3>Options</h3>
          <div>No items configured.</div>
        </div>
      );
    }

    const groups = groupedData ? Object.keys(groupedData) : [];

    return (
      <div>
        <div className="medium-12">
          {isLoading ? <img style={{ display: "block", marginLeft: "auto", marginRight: "auto" }} src={path + `images/ajax-loader.gif`} alt="" /> : null}
          {isError ? <div style={{ color: "red", textAlign: "center" }} className="cell pt5">{errorMsg}</div> : responseMsg ? <div style={{ color: "green", textAlign: "center" }} className="cell pt5">{responseMsg}</div> : null}
        </div>

        <div className="medium-12">
          <button
            className={classNames("float-right", "pr25")}
            onClick={this.onSave}
            disabled={!hasChanges || isSaveInProgress}
            title={this.props.ui && this.props.ui.save}
            type="button"
          >
            <img width="20" src="images/save_active.svg" alt="Save" />
          </button>
        </div>

        <ul style={{ listStyleType: "disc", paddingLeft: 20, textAlign: "left", margin: 0 }}>
          {groups.map((g) => {
            const group = groupedData[g] || { items: [], available: false };
            const items = group.items || [];
            const allChecked = items.length > 0 && items.every((it) => !!it.fieldAvailable);
            const noneChecked = items.length === 0 || items.every((it) => !it.fieldAvailable);
            // checked is true only when all children are checked.
            const checked = allChecked;

            return (
              <li key={g} style={{ marginBottom: 8 }}>
                <label style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                  <input
                    ref={(el) => { this.parentCheckboxRefs[g] = el; }}
                    type="checkbox"
                    // checked true only when all children are checked; indeterminate handled in componentDidUpdate
                    checked={checked}
                    //disabled = {this.props.spendDetails.currentActiveRequest.SOLUTION_IN_SCOPE == 'RISE'}
                    onChange={() => this.toggleSavingAvailability(g)}
                    aria-checked={noneChecked ? "false" : allChecked ? "true" : "mixed"}
                  />
                  <span>{g}</span>
                </label>

                <ul style={{ listStyleType: "circle", marginLeft: 20, paddingLeft: 0, marginTop: 6 }}>
                  {items.map((it, i) => (
                    <li key={it.id} style={{ marginBottom: 4 }}>
                      <label style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                        <input
                          type="checkbox"
                          checked={!!it.fieldAvailable}
                          //disabled = {this.props.spendDetails.currentActiveRequest.SOLUTION_IN_SCOPE == 'RISE'}
                          onChange={() => this.toggleHeaderAvailability(g, i)}
                        />
                        <span>{it.headerName}</span>
                      </label>
                    </li>
                  ))}
                </ul>
              </li>
            );
          })}
        </ul>
      </div>
    );
  }
}

const mapStateToProps = (state) => {
  return {
    ui: state.content.ui,
    userData: state.valueStory,
    user: state.default,
    content: state.content.spendAnalysis.worldMap,
    focusLabel: state.content.spendAnalysis.enablement.focusLabel,
    spendDetails: state.spendDetails,
    currencySymbols: state.content.CurrencySymbols,
    kpiFilters: state.kpis.kpiBenchMarkFilters,
    kpiLables: state.content.kpiTab,
  };
};
const mapDispatchToProps = (dispatch) => {
  return {
    resetError: (error) => dispatch(resetError(error)),
    defineScopeLoading: (bool) => dispatch(defineScopeLoading(bool)),
    GetRiseSavingDetails: (DMCId, VSId, userEmail) => dispatch(GetRiseSavingDetails(DMCId, VSId, userEmail)),
    // UpdateRiseSavingDetails: (VS_ID,DMC_ID, SAVING_TAB_NAME, HEADER_FIELD_NAME, FIELD_AVAILABLE) =>
    //   dispatch(UpdateRiseSavingDetails(VS_ID,DMC_ID,SAVING_TAB_NAME,HEADER_FIELD_NAME,FIELD_AVAILABLE)),
    UpdateRiseSavingDetails: (data) => dispatch(UpdateRiseSavingDetails(data)),
    pendingChanges: changes => dispatch(pendingChanges(changes)),
  };
};
export default connect(mapStateToProps, mapDispatchToProps)(SavingOptions);
